import { useEffect, useRef, useState } from 'react';
import { Card, Button, Input } from '@/shared/ui';
import type { ApiError } from '@/shared/api/errors';
import { useInsuranceCredentials } from '../hooks/useInsuranceCredentials';
import styles from './LocationCredentialsCard.module.css';

function fieldErrorsOf(error: unknown): Record<string, string[]> {
  const data = (error as ApiError | null)?.data as
    | { error?: { errors?: Record<string, string[]> } }
    | undefined;
  return data?.error?.errors ?? {};
}

export function LocationCredentialsCard() {
  const { credentials, isLoading, save } = useInsuranceCredentials();
  const hydrated = useRef(false);

  const [legalName, setLegalName] = useState('');
  const [tin, setTin] = useState('');
  const [groupNpi, setGroupNpi] = useState('');
  const [fax, setFax] = useState('');

  useEffect(() => {
    if (hydrated.current || !credentials) return;
    hydrated.current = true;
    setLegalName(credentials.legalName);
    setGroupNpi(credentials.groupNpi ?? '');
    setFax(credentials.fax ?? '');
  }, [credentials]);

  const tinPlaceholder = credentials ? `••-•••${credentials.tinLast4}` : undefined;

  function handleSave() {
    save.mutate(
      {
        legalName,
        groupNpi: groupNpi || undefined,
        fax: fax || undefined,
        ...(tin ? { tin } : {}),
      },
      { onSuccess: () => setTin('') },
    );
  }

  const fieldErrors = fieldErrorsOf(save.error);

  return (
    <Card title="Location credentials">
      {isLoading ? null : (
        <div className={styles.grid}>
          <Input
            label="Legal / billing entity name *"
            value={legalName}
            onChange={(e) => setLegalName(e.target.value)}
            hint="Exactly as on payer contracts."
            error={fieldErrors.legalName?.[0]}
            fullWidth
          />
          <Input
            label="Tax ID (TIN) *"
            className={styles.mono}
            value={tin}
            onChange={(e) => setTin(e.target.value)}
            placeholder={tinPlaceholder}
            hint="Encrypted; shown masked after entry."
            error={fieldErrors.tin?.[0]}
            fullWidth
          />
          <Input
            label="Group NPI (Type 2)"
            className={styles.mono}
            value={groupNpi}
            onChange={(e) => setGroupNpi(e.target.value)}
            hint="Optional — only if this office has one distinct from an individual provider's NPI."
            error={fieldErrors.groupNpi?.[0]}
            fullWidth
          />
          <Input
            label="Fax"
            className={styles.mono}
            value={fax}
            onChange={(e) => setFax(e.target.value)}
            fullWidth
          />
          <div className={styles.fullWidth}>
            <Button onClick={handleSave} isLoading={save.isPending}>
              Save changes
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}
